import React, { createContext, ReactNode, useCallback, useContext, useRef, useState } from 'react'
import ApiClient from 'api-client/ApiClient'
import { useAppSelector } from 'app/store'
import useListAction from 'core/hooks/useListAction'
import { listResmgrHosts } from '../../actions'
import { resmgrHostsById } from '../../selectors'
import { VMHAHostStatus } from './types'
import {
  createVMHAHostStatus,
  getLatestEventForHost,
  parseVMHAEventsResponse,
  parseVMHATaskDetailsResponse,
} from './vmhaHelpers'

interface VMHAContextValue {
  vmhaStatuses: Map<string, VMHAHostStatus>
  loading: boolean
  reloading: boolean
  lastFetchTime: number | null
  fetchVMHAData: (force?: boolean) => Promise<void>
  reloadHostVMHAStatus: (hostId: string) => Promise<void>
  reloadVMHATaskDetails: (hostId: string) => Promise<void>
}

const VMHAContext = createContext<VMHAContextValue | undefined>(undefined)

const CACHE_DURATION_MS = 30 * 1000 // 30 seconds

const { vmha } = ApiClient.getInstance()

interface VMHAProviderProps {
  children: ReactNode
}

export const VMHAProvider: React.FC<VMHAProviderProps> = ({ children }) => {
  const [vmhaStatuses, setVmhaStatuses] = useState<Map<string, VMHAHostStatus>>(new Map())
  const [loading, setLoading] = useState(false)
  const [reloading, setReloading] = useState(false)
  const [lastFetchTime, setLastFetchTime] = useState<number | null>(null)
  const fetchInProgressRef = useRef(false)

  useListAction(listResmgrHosts)
  const resMgrHosts = useAppSelector(resmgrHostsById)

  const fetchHostVMHAStatus = useCallback(
    async (hostId: string, events: any[]): Promise<VMHAHostStatus | null> => {
      try {
        const latestEvent = getLatestEventForHost(events, resMgrHosts)

        if (!latestEvent) {
          return null
        }

        // Fetch task details for the latest event
        const taskDetailsResponse = await vmha.getVMHATaskDetails(
          latestEvent.vmhaUuid,
          latestEvent.eventTimestamp,
        )
        const tasks = parseVMHATaskDetailsResponse(taskDetailsResponse)

        // Create host status with state calculation
        return createVMHAHostStatus(latestEvent, tasks)
      } catch (error) {
        return null
      }
    },
    [resMgrHosts],
  )

  const fetchVMHAData = useCallback(
    async (force: boolean = false) => {
      const now = Date.now()

      // Check if we should use cached data
      if (!force && lastFetchTime && now - lastFetchTime < CACHE_DURATION_MS) {
        return
      }

      // Prevent concurrent fetches
      if (fetchInProgressRef.current) {
        return
      }

      try {
        fetchInProgressRef.current = true
        setLoading(true)

        // Step 1: Fetch all VMHA events
        const eventsResponse = await vmha.getVMHAEvents()

        // Step 2: Parse events by host
        const eventsByHost = parseVMHAEventsResponse(eventsResponse)

        if (eventsByHost.size === 0) {
          setVmhaStatuses(new Map())
          setLastFetchTime(now)
          return
        }

        // Step 3: For each host, get the latest event and fetch task details
        const statusesMap = new Map<string, VMHAHostStatus>()

        await Promise.allSettled(
          Array.from(eventsByHost.entries()).map(async ([hostId, events]) => {
            const hostStatus = await fetchHostVMHAStatus(hostId, events)
            if (hostStatus) {
              statusesMap.set(hostId, hostStatus)
            }
          }),
        )

        setVmhaStatuses(statusesMap)
        setLastFetchTime(now)
      } catch (error) {
        return
      } finally {
        setLoading(false)
        fetchInProgressRef.current = false
      }
    },
    [lastFetchTime, resMgrHosts, fetchHostVMHAStatus],
  )

  // Reload VMHA Status for each individual host
  const reloadHostVMHAStatus = useCallback(
    async (hostId: string): Promise<void> => {
      try {
        setReloading(true)

        // Fetch events for this specific host
        const eventsResponse = await vmha.getVMHAEvents()
        const eventsByHost = parseVMHAEventsResponse(eventsResponse)
        const events = eventsByHost.get(hostId)

        if (!events || events.length === 0) {
          // Remove host from statuses if no events
          setVmhaStatuses((prev) => {
            const newMap = new Map(prev)
            newMap.delete(hostId)
            return newMap
          })
          return
        }

        const hostStatus = await fetchHostVMHAStatus(hostId, events)

        if (hostStatus) {
          setVmhaStatuses((prev) => {
            const newMap = new Map(prev)
            newMap.set(hostId, hostStatus)
            return newMap
          })
        }
      } catch (error) {
        return
      } finally {
        setReloading(false)
      }
    },
    [fetchHostVMHAStatus],
  )

  // Reload VMHA Task Details for each individual host
  const reloadVMHATaskDetails = useCallback(
    async (hostId: string): Promise<void> => {
      try {
        const currentStatus = vmhaStatuses.get(hostId)

        if (!currentStatus) {
          return
        }

        setReloading(true)

        // Fetch updated task details using existing event info
        const taskDetailsResponse = await vmha.getVMHATaskDetails(
          currentStatus.event.vmhaUuid,
          currentStatus.event.eventTimestamp,
        )
        const tasks = parseVMHATaskDetailsResponse(taskDetailsResponse)

        // Create updated host status with new task details
        const updatedStatus = createVMHAHostStatus(currentStatus.event, tasks)

        setVmhaStatuses((prev) => {
          const newMap = new Map(prev)
          newMap.set(hostId, updatedStatus)
          return newMap
        })
      } catch (error) {
        return
      } finally {
        setReloading(false)
      }
    },
    [vmhaStatuses],
  )

  const value: VMHAContextValue = {
    vmhaStatuses,
    loading,
    reloading,
    lastFetchTime,
    fetchVMHAData,
    reloadHostVMHAStatus,
    reloadVMHATaskDetails,
  }

  return <VMHAContext.Provider value={value}>{children}</VMHAContext.Provider>
}

export const useVMHAContext = (): VMHAContextValue => {
  const context = useContext(VMHAContext)
  if (context === undefined) {
    throw new Error('useVMHAContext must be used within a VMHAProvider')
  }
  return context
}
